import { useMemo } from 'react'

import { type ProspectEmployee } from 'components/prospects/types'

import { type QuoteAllowanceParams } from './useCreateQuoteAllowances'

export type FlatAllowanceCalculations = {
  totalCost: number
  totalEmployerCost: number
  totalEmployeeCost: number
  coveredEmployeeCount: number
  currentTotalCoverageCost: number
  currentTotalEmployerCost: number
  currentTotalEmployeeCost: number
  costsByEmployeeId: Record<string, ReturnType<typeof calculateCosts>>
}

export const parseEmployeeData = (employee: ProspectEmployee) => {
  const employeeSpouseCount = employee.dependents.filter((dependent) => dependent.relationship === 'spouse').length
  const employeeChildCount = employee.dependents.filter((dependent) => dependent.relationship === 'child').length
  const currentEmployerCost = (employee.employer_medical_contribution || 0)
  const currentEmployeeCost = (employee.employee_medical_contribution || 0)
  const currentCoverageCost = currentEmployerCost + currentEmployeeCost

  return {
    employeeSpouseCount,
    employeeChildCount,
    currentCoverageCost,
    currentEmployerCost,
    currentEmployeeCost,
  }
}

export const calculateCosts = (employee: ProspectEmployee, allowance?: QuoteAllowanceParams) => {
  const { employeeSpouseCount, employeeChildCount, currentCoverageCost, currentEmployerCost, currentEmployeeCost } = parseEmployeeData(employee)

  if (!allowance?.flat_config) {
    return {
      employeeCost: 0,
      employerCost: 0,
      employeeAllowanceAmount: 0,
      currentCoverageCost,
      currentEmployerCost,
      currentEmployeeCost,
    }
  }

  const coveredChildCount = Math.max(0, Math.min(employeeChildCount, allowance.flat_config.max_dependent_count - employeeSpouseCount))

  const employeeAllowanceAmount = allowance.flat_config.employee_amount || 0
  const employerCost = employeeAllowanceAmount + ((allowance.flat_config.spouse_amount || 0) * employeeSpouseCount) + ((allowance.flat_config.dependent_amount || 0) * coveredChildCount)
  const employeeCost = Math.max(currentCoverageCost - employerCost, 0)

  return {
    employeeCost,
    employerCost,
    employeeAllowanceAmount,
    currentCoverageCost,
    currentEmployerCost,
    currentEmployeeCost,
  }
}

const useFlatAllowanceCalculations = (
  employees: ProspectEmployee[],
  allowances: QuoteAllowanceParams[]
): FlatAllowanceCalculations => {
  const defaultAllowance = useMemo(() => {
    const defaultIndex = allowances.findIndex(allowance => !allowance.class)
    return allowances[defaultIndex]
  }, [JSON.stringify(allowances)])

  const allowanceByState = useMemo(() => {
    return allowances.reduce((acc, allowance) => {
      const state = allowance.class?.location_id
      if (!state) {
        return acc
      }

      acc[state] = allowance
      return acc
    }, {} as Record<string, QuoteAllowanceParams>)
  }, [JSON.stringify(allowances)])

  return useMemo(() => {
    let totalCost = 0
    let totalEmployerCost = 0
    let totalEmployeeCost = 0
    let coveredEmployeeCount = 0
    let currentTotalCoverageCost = 0
    let currentTotalEmployerCost = 0
    let currentTotalEmployeeCost = 0
    const costsByEmployeeId: Record<string, ReturnType<typeof calculateCosts>> = {}

    employees.forEach((employee) => {
      if (employee.employee_medical_contribution || employee.employer_medical_contribution) {
        coveredEmployeeCount++
      }
      const state = employee.rating_area_code.substring(0, 2)
      const allowance = allowanceByState[state] || defaultAllowance
      const { employeeCost, employerCost, employeeAllowanceAmount, currentCoverageCost, currentEmployerCost, currentEmployeeCost } = calculateCosts(employee, allowance)
      costsByEmployeeId[employee.id] = {
        employeeCost,
        employerCost,
        employeeAllowanceAmount,
        currentCoverageCost,
        currentEmployerCost,
        currentEmployeeCost,
      }

      totalCost += employeeCost + employerCost
      totalEmployerCost += employerCost
      totalEmployeeCost += employeeCost
      currentTotalCoverageCost += currentCoverageCost
      currentTotalEmployerCost += currentEmployerCost
      currentTotalEmployeeCost += currentEmployeeCost
    })

    return {
      totalCost,
      totalEmployerCost,
      totalEmployeeCost,
      coveredEmployeeCount,
      currentTotalCoverageCost,
      currentTotalEmployerCost,
      currentTotalEmployeeCost,
      costsByEmployeeId,
    }
  }, [employees, JSON.stringify(allowanceByState), JSON.stringify(defaultAllowance)])
}

export default useFlatAllowanceCalculations
